import React, { useEffect, useState } from 'react'
import { Col, Form, Input, Row, Select } from 'antd'
import { compose } from 'recompose'
import { get, range, isEmpty } from 'lodash'
import CurrencySelect from '../../../elements/Select/Currency'
import { defaultFormItemLayout, fullWidthFormItemLayout, rules } from '../../../modules/FormItems'
import CentersCascader from '../../../elements/Cascader/CentersCascader'
import ClientsSelect from '../../../elements/Select/Client/ClientsSelect'
import useFetchSelectUrl from '../../../elements/Hooks/useFetchSelectUrl'
import useFetchObject from '../../../elements/Hooks/useFetchObject'
import SalesContractsApi from '../../../api/Sales/SalesContracts'
import AccountsCascader from '../../../elements/Cascader/AccountsCascader'
import { BUDGET_TYPE_INTERNAL, BUDGET_TYPE_SALES } from '../../../components/Common/Budgets/BudgetCont'
import withData from '../../../elements/Select/withData'
import CustomSelect from '../../../elements/Select/CustomSelect'
import { strVal } from '../../../modules/Utils'

const { TextArea } = Input
const salesContractsApi = new SalesContractsApi()
const currentYear = new Date().getFullYear()
export const FiscalYearSelect = compose(
  withData({
    data: range(currentYear, currentYear + 10).map((n) => ({ key: n, value: n })),
  })
)(CustomSelect)

function BudgetForm(props) {
  const { getFieldValue, setFieldsValue, resetFields, getFieldDecorator } = props.form
  const initialWidth = 10
  const { budget, list, showList, option } = props
  const [titleOptions, setTitleOptions] = useState({
    centers: '',
    accounts: '',
    fiscal: '',
  })
  const client = getFieldValue('client._id') || ''
  const [ContractsSelect] = useFetchSelectUrl(
    {
      name: 'clients_contracts',
      url: `clients/${client.replace('partners/', '')}/contracts?state=active`,
      cache: true,
    },
    [client],
    isEmpty(client),
    Select,
    (c) => ({ ...c, display: `[${c.code}]  ${c.title}` }),
    () => resetFields(['sales._id', 'title'])
  )
  const contractID = getFieldValue('contract._id') || ''
  const contractKey = contractID.replace('salesContracts/', '')
  const { object: contract } = useFetchObject(
    () => salesContractsApi.get(contractKey),
    false,
    [contractKey],
    isEmpty(contractKey)
  )

  const [SalesSelect] = useFetchSelectUrl(
    {
      name: 'clients_sales',
      url: `sales/?select=_id,code,title&contract=${contractID}`,
      cache: true,
    },
    [contractID],
    isEmpty(contractID),
    Select,
    (c) => ({ ...c, display: `[${c.code}]  ${c.title}` })
  )
  function generateTitle(tOps) {
    const title = `${tOps.fiscal ? `[${tOps.fiscal}] ` : ''}${tOps.centers}${
      tOps.accounts ? ` - ${tOps.accounts}` : ''
    }`
    setFieldsValue({
      title,
    })
    setTitleOptions(tOps)
  }
  function onBlur() {
    resetFields('sales._id')
    if (isEmpty(getFieldValue('title')))
      setFieldsValue({
        centers: get(contract, 'budget.cost._id'),
        currency: get(contract, 'pay.currency'),
        title: get(contract, 'title'),
      })
  }

  return (
    <Row>
      <Form>
        <Col span={24}>
          <Form.Item {...fullWidthFormItemLayout} label={'Title'}>
            {getFieldDecorator('title', {
              initialValue: get(budget, 'title'),
              rules: [{ required: true, message: "Title can't be empty" }],
            })(<Input placeholder={'Title'} />)}
          </Form.Item>
        </Col>
        {option === BUDGET_TYPE_SALES && showList && (
          <Row>
            <Col span={12}>
              <Form.Item {...defaultFormItemLayout} label="Select a Client">
                {getFieldDecorator('client._id', {
                  initialValue: get(budget, 'client._id'),
                  rules: rules.select,
                })(<ClientsSelect placeholder={'Select a Client'} keyAtr={'_id'} valueAtr={'fn'} />)}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item {...defaultFormItemLayout} label="Select a Contract">
                {getFieldDecorator('contract._id', {
                  initialValue: get(budget, 'contract._id'),
                  rules: rules.select,
                })(
                  <ContractsSelect
                    keyAtr={'_id'}
                    valueAtr={'display'}
                    onBlur={onBlur}
                    placeholder={'Contracts Account'}
                  />
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item {...defaultFormItemLayout} label="Select a Sales">
                {getFieldDecorator('sales._id', {
                  initialValue: get(budget, 'sales._id'),
                })(<SalesSelect keyAtr={'_id'} valueAtr={'display'} />)}
              </Form.Item>
            </Col>
          </Row>
        )}
        <Row>
          {option === BUDGET_TYPE_INTERNAL && (
            <Col span={12}>
              <Form.Item label="Cost Center" {...defaultFormItemLayout}>
                {getFieldDecorator('centers', {
                  initialValue: get(budget, 'centers'),
                  rules: [
                    {
                      type: 'array',
                      required: true,
                      message: "Center can't be empty ",
                    },
                  ],
                })(
                  <CentersCascader
                    onChange={(value, selectedOptions) => {
                      const tOps = {
                        ...titleOptions,
                        centers: selectedOptions.map((s) => s.name).join(' / '),
                      }
                      generateTitle(tOps)
                    }}
                    disabled={list.length > 0}
                    key={contract._key}
                    company={get(contract, 'company._id')}
                    category={'Cost'}
                  />
                )}
              </Form.Item>
            </Col>
          )}
          {option === BUDGET_TYPE_INTERNAL && getFieldValue('centers') && (
            <>
              <Col span={12}>
                <Form.Item {...defaultFormItemLayout} label={'Fiscal Year'}>
                  {getFieldDecorator('fiscal', {
                    initialValue: get(budget, 'fiscal'),
                    rules: rules.select,
                  })(
                    <FiscalYearSelect
                      onChange={(f) => {
                        const tOps = {
                          ...titleOptions,
                          fiscal: f,
                        }
                        generateTitle(tOps)
                      }}
                      disabled={list.length > 0}
                      placeholder={'Fiscal Year'}
                    />
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item {...defaultFormItemLayout} label={'Account'}>
                  {getFieldDecorator('accounts', {
                    initialValue: get(budget, 'accounts'),
                    rules: rules.select,
                  })(
                    <AccountsCascader
                      onChange={(value, selectedOptions) => {
                        const tOps = {
                          ...titleOptions,
                          accounts: selectedOptions.map((s) => s.name).join(' / '),
                        }
                        generateTitle(tOps)
                      }}
                      disabled={list.length > 0}
                      center={getFieldValue('centers')[getFieldValue('centers').length - 1]}
                      placeholder={'Account'}
                    />
                  )}
                </Form.Item>
              </Col>
            </>
          )}
          <Col span={12}>
            <Form.Item {...defaultFormItemLayout} label={'Currency'}>
              {getFieldDecorator('currency', {
                initialValue: get(budget, 'currency'),
                rules: rules.select,
              })(<CurrencySelect placeholder={'Currency'} />)}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Notes" {...fullWidthFormItemLayout}>
              {getFieldDecorator('notes', {
                initialValue: strVal(budget, 'notes'),
              })(<TextArea placeholder="Budget Notes" />)}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Row>
  )
}

BudgetForm.propTypes = {}
BudgetForm.defaultProps = {
  budget: {},
}

export default compose(Form.create())(BudgetForm)
